import React from 'react'

/**
 * IconData component renders a collection of SVG symbols. These are then used on screen by the Icon component.
  
 * This component includes various SVG symbols such as caret-down, persist, atom logos, kit elements, gender icons, 
 * coach, star, video play, reset filters, video segments, streak, levels, demo video, upload image, kit accessories, 
 * video controls, bookmark, rank, RPM element, app icon, group image placeholder, iron compass, and dumbbell.

 * The SVG elements use the following CSS color variables:
 * 
 * - `--icon-fg`: Default foreground color for icons.
 * - `--white`: White color, used as a fallback for `--icon-fg`.
 * - `--icon-circle-fill`: Fill color for circular elements.
 * - `--icon-circle-stroke`: Stroke color for circular elements.
 * - `--icon-stroke-width`: Stroke width for circular elements.
 * - `--icon-line-stroke`: Stroke color for line elements.
 * - `--icon-line-width`: Stroke width for line elements.
 * - `--icon-element-fill`: Fill color for icon elements.
 * - `--icon-element-outline`: Outline color for icon elements.
 * - `--icon-element-fill`: Fill color for icon elements.
 * - `--brand-base`: Base color for brand elements.
 * - `--brand-black`: Black color for brand elements.
 * - `--brand-gray-3`: Gray color (level 3) for brand elements.
 * - `--brand-gray-6`: Gray color (level 6) for brand elements.
 * - `--gold-base`: Base color for gold elements.
 * - `--brand-gray-6`: Gray color (level 6) for brand elements.
 * - `--brand-base`: Base color for brand elements.
 * 
 * @component
 * @example
 * return (
 *   <IconData />
 * )
 */

function IconData() {
  /* eslint-disable react/no-unknown-property */

  return (
    <svg
      id="svg-symbols"
      xmlns="http://www.w3.org/2000/svg"
      width="0"
      height="0"
      display="none"
    >
      <symbol id="svg-caret-down" viewBox="0 0 100 100">
        <polyline
          points="10 35 50 70 90 35"
          strokeWidth="8"
          fill="transparent"
        />
      </symbol>
      <symbol id="svg-persist" viewBox="0 0 100 41">
        <rect x="0" y="0.43" width="33.21" height="12.95" />
        <rect y="14.96" width="22.19" height="12.11" />
        <rect x="0" y="28.92" width="11.16" height="11.81" />
        <rect x="35.08" y="0.36" width="25.05" height="13.25" />
        <rect x="35.08" y="15.18" width="31.57" height="25.46" />
        <rect x="68.43" y="0.27" width="25.05" height="13.25" />
        <rect x="68.43" y="15.09" width="31.57" height="25.46" />
      </symbol>
      <symbol id="svg-atom-a-contained" viewBox="0 0 54 41">
        <polygon
          className="logo logo--atom logo--atom--a-contained"
          fill="var(--icon-fg, var(--white))"
          points="19.55,25.98 34.48,25.98 34.48,23.22 19.55,23.19"
        />
        <path
          className="logo logo--atom logo--atom--a-contained"
          d="M47.74,0H0.28v34.46L6.16,41h47.56V7.03L47.74,0z M41.35,31.35l-6.87,0l-18.66,0l-3.17-3.48V17.82h21.83v-2.79
		H12.65V9.65h25.47l3.22,3.77V31.35z"
        />
      </symbol>
      <symbol id="svg-logo-atom-a" viewBox="0 0 52 40">
        <path
          className="logo logo--atom logo--atom--a"
          d="M0 0H46.1888L52 6.93879V40H5.71098L0 33.5714V15H39.5761V9.89797H0V0ZM12.5241 30.2041H39.5761V25.102L12.5241 25V30.2041Z"
        />
      </symbol>
      <symbol id="svg-logo-atom" viewBox="0 0 355 40">
        <path
          className="logo logo--atom logo--atom--a"
          d="M0 0H46.1888L52 6.93879V40H5.71098L0 33.5714V15H39.5761V9.89797H0V0ZM12.5241 30.2041H39.5761V25.102L12.5241 25V30.2041Z"
        />
        <path
          className="logo logo--atom logo--atom--t"
          d="M99 0H151V9.94925H131.262V40H118.838V9.94925H99V0Z"
        />
        <path
          className="logo logo--atom logo--atom--o"
          d="M198 0H244.155L250 6.88609V40H203.744L198 33.6203V0ZM209.488 29.9747H238.512V9.92407H209.488V29.9747Z"
        />
        <path
          className="logo logo--atom logo--atom--m"
          d="M343.719 0L325.95 20L308.181 0H297V40H309.678V19.901L319.162 29.4059H332.738L342.322 19.901V40H355V0H343.719Z"
        />
      </symbol>

      <symbol id="svg-axis-kit" viewBox="0 0 100 100">
        <path
          className="element element--kit element--kit--axis"
          d="M82.1776226,62.9562079 C78.9650064,62.6492016 75.9085119,63.2165803 73.2146851,64.4465487 C72.513592,64.766185 71.7029228,64.7263519 71.0755268,64.2784724 L55.7949936,50.4738752 C55.208325,50.0561135 54.8805664,49.3624346 54.9261423,48.6425243 L55.7426298,37.4270784 C55.7911148,36.669278 56.2362071,35.9755992 56.931482,35.6744221 C63.4449572,32.8559876 67.9317593,26.2077464 67.5080004,18.5685355 C67.0144231,9.66243776 59.6194905,2.37395242 50.7195835,2.014483 C40.7229458,1.61226587 32.4630408,9.64009237 32.4630408,19.5643629 C32.4630408,26.7741536 36.8315395,32.9677145 43.0511956,35.6676213 C43.7551978,35.9736561 44.2051386,36.6760788 44.2545934,37.442623 L45.0691414,48.6425243 C45.115687,49.3624346 44.7869587,50.0561135 44.2012598,50.4738752 L28.9236357,64.2784724 C28.2962397,64.7263519 27.4855705,64.766185 26.7844774,64.4465487 C24.0906507,63.2165803 21.0341561,62.6492016 17.824449,62.9562079 C9.81278723,63.7217806 3.21397844,70.0630155 2.15797509,78.0539235 C0.62487932,89.6482695 10.4721833,99.4530408 22.0639776,97.8218269 C29.3124854,96.8007394 35.2548073,91.1337526 36.7151755,83.9501935 C37.2679045,81.2259982 37.1515405,78.6008999 36.5386901,76.1701094 C36.3447501,75.4074513 36.6269328,74.6049601 37.2300862,74.1026745 L48.6657588,61.6698903 C49.4376401,61.02576 50.5586133,61.02576 51.3295249,61.6689188 L62.7681066,74.1026745 C63.37126,74.6059317 63.652473,75.4084229 63.4604724,76.171081 C62.847622,78.5960422 62.731258,81.2143397 63.2801082,83.9327058 C64.7307795,91.1162649 70.6663134,96.789081 77.9119121,97.8179407 C89.5124338,99.4656708 99.3713741,89.6589565 97.8431268,78.0607242 C96.7900326,70.0669016 90.1912238,63.7227521 82.1776226,62.9562079"
        />
      </symbol>

      <symbol id="svg-fly-kit" viewBox="0 0 108 100">
        <path
          className="element element--kit element--kit--fly"
          d="M54,95.5332297 C62.6378415,95.5332297 68.3690557,100 69.5880921,100 C70.8091791,100 70.9322105,99.2173433 70.6215561,98.5032329 C52.2406603,56.1957767 75.2372853,15.4587494 105.016018,70.7337535 C105.596316,71.8110575 107.260316,71.564495 107.500228,70.3736291 C111.625882,49.8782534 89.538664,-2.17404649 65.951488,16.5401457 C65.7372083,16.7099771 65.4993475,16.8266083 65.2543099,16.9166394 C63.9829851,14.1307906 55.3677092,0 54,0 C52.6323103,0 44.0160091,14.1307906 42.7457096,16.9166394 C42.500672,16.8266083 42.2617859,16.7099771 42.0475062,16.5401457 C18.4603303,-2.17404649 -3.62586221,49.8782534 0.499791787,70.3736291 C0.739703078,71.564495 2.40267793,71.8110575 2.98400144,70.7337535 C32.7627342,15.4587494 55.7593592,56.1957767 37.3784634,98.5032329 C37.067809,99.2173433 37.1908404,100 38.4109021,100 C39.6309638,100 45.362178,95.5332297 54,95.5332297"
        />
      </symbol>

      <symbol id="svg-power-kit" viewBox="0 0 66 100">
        <path
          className="element element--kit element--kit--power"
          d="M63.9974152,34.9926405 L37.086592,34.9926405 L50.6292412,4.40598581 C50.9216855,3.74475486 50.4389046,3 49.7182029,3 L16.1931819,3 C15.7470804,3 15.3545111,3.29929401 15.2335681,3.73083421 L1.07529883,54.3562669 C0.718417655,55.6310008 1.67505747,56.8947971 2.99551781,56.8947971 L28.9407792,56.8947971 C28.9407792,56.8947971 20.988277,81.4150307 16.2635668,97.0548828 C15.7797945,98.6547634 17.8734974,99.7306309 18.8806954,98.4002143 C34.1096082,78.2669747 59.2201641,43.9943304 64.7974238,36.5885437 C65.2940834,35.9302958 64.8202245,34.9926405 63.9974152,34.9926405"
        />
      </symbol>

      <symbol id="svg-male" viewBox="0 0 100 100">
        <path
          className="element element--gender element--gender--male"
          d="M77.1675 57.2129C77.1587 50.9851 75.494 44.8729 72.3459 39.5093L71.4549 37.9813L80.7837 28.4972L91.2655 39.0351C91.3018 39.1009 91.3549 39.1559 91.4194 39.1941C91.484 39.2323 91.5575 39.2525 91.6324 39.2525C91.7073 39.2525 91.7808 39.2323 91.8453 39.1941C91.9098 39.1559 91.963 39.1009 91.9992 39.0351V7.42151C92.0039 7.33898 91.987 7.25666 91.9502 7.18272C91.9134 7.10878 91.8581 7.04579 91.7896 7L60.3442 7C60.2603 7.00301 60.1793 7.03126 60.1115 7.08108C60.0438 7.13091 59.9925 7.20002 59.9643 7.27947C59.9361 7.35893 59.9322 7.44507 59.9532 7.52675C59.9742 7.60844 60.0191 7.68191 60.0821 7.73765L70.5639 18.2755L61.3924 28.0757L59.8201 26.9692C52.5078 22.8717 43.9727 21.5572 35.7756 23.266C27.5786 24.9749 20.2671 29.5929 15.1784 36.2757C10.0896 42.9584 7.56343 51.2594 8.06186 59.6607C8.5603 68.062 12.05 76.0022 17.8928 82.0295C22.7898 86.8314 28.9824 90.0809 35.701 91.374C42.4196 92.6672 49.3681 91.9471 55.6831 89.3032C61.998 86.6593 67.401 82.2082 71.2206 76.5029C75.0403 70.7977 77.1082 64.0898 77.1675 57.2129V57.2129ZM42.2631 77.9197C38.8767 77.9179 35.5431 77.0766 32.5572 75.4705C29.5714 73.8644 27.0256 71.5429 25.1451 68.7116C23.2646 65.8803 22.1076 62.6266 21.7763 59.2384C21.445 55.8503 21.9498 52.4324 23.246 49.2872C24.5421 46.142 26.5896 43.3666 29.2072 41.2067C31.8249 39.0469 34.9318 37.5692 38.253 36.9045C41.5741 36.2398 45.0071 36.4086 48.2479 37.396C51.4887 38.3834 54.4373 40.1589 56.8328 42.5652C59.7155 45.4612 61.679 49.1518 62.4747 53.17C63.2703 57.1883 62.8624 61.3535 61.3026 65.1386C59.7427 68.9236 57.1011 72.1584 53.7119 74.4336C50.3227 76.7087 46.3383 77.922 42.2631 77.9197V77.9197Z"
        />
      </symbol>

      <symbol id="svg-female" viewBox="0 0 100 100">
        <path
          className="element element--gender element--gender--female"
          d="M84 35.3199C84 15.82 68.3293 0 49 0C29.6707 0 14 15.82 14 35.3199C14 52.6499 26.3667 66.9998 42.6627 69.9998V76.7398H33.6C33.4963 76.7358 33.3928 76.7539 33.2957 76.7931C33.1985 76.8323 33.1096 76.8917 33.0341 76.968C32.9585 77.0443 32.8979 77.1359 32.8556 77.2375C32.8134 77.339 32.7904 77.4486 32.788 77.5598V88.7498C32.7916 88.8601 32.8155 88.9686 32.8583 89.069C32.901 89.1695 32.9619 89.2599 33.0373 89.3351C33.1127 89.4103 33.2012 89.4689 33.2978 89.5075C33.3943 89.546 33.497 89.5638 33.6 89.5598H42.7093V99.1798C42.7166 99.4034 42.806 99.615 42.958 99.7685C43.1101 99.9221 43.3125 100.005 43.5213 99.9997H54.5253C54.6283 100.002 54.7307 99.9833 54.8268 99.9435C54.9229 99.9038 55.0107 99.8441 55.0852 99.7679C55.1598 99.6918 55.2196 99.6006 55.2612 99.4997C55.3029 99.3988 55.3256 99.2901 55.328 99.1798V89.5598H64.4467C64.5496 89.5638 64.6523 89.546 64.7489 89.5075C64.8455 89.4689 64.934 89.4103 65.0094 89.3351C65.0848 89.2599 65.1456 89.1695 65.1884 89.069C65.2312 88.9686 65.2551 88.8601 65.2587 88.7498V77.5998C65.2563 77.4886 65.2333 77.379 65.191 77.2775C65.1488 77.1759 65.0881 77.0843 65.0126 77.008C64.9371 76.9317 64.8481 76.8723 64.751 76.8331C64.6538 76.7939 64.5504 76.7758 64.4467 76.7798H55.328V69.9998C71.6333 66.9998 84 52.6499 84 35.3199ZM34.3747 50.0899C32.471 48.209 30.9503 45.9272 29.908 43.3873C28.8657 40.8475 28.3242 38.1044 28.3173 35.3299C28.3173 23.8199 37.5947 14.45 49 14.45C60.4053 14.45 69.6827 23.8199 69.6827 35.3199C69.6752 38.0952 69.1325 40.839 68.0885 43.3789C67.0445 45.9189 65.5218 48.2003 63.616 50.0799C59.6317 54.0125 54.4126 56.1957 48.9953 56.1957C43.578 56.1957 38.3589 54.0125 34.3747 50.0799V50.0899Z"
        />
      </symbol>

      <symbol id="svg-coach" viewBox="0 0 8.45 11.3">
        <polygon
          className="element element--coach element--coach-inside"
          points="5.72 9.5 2.94 9.5 1.91 8.59 1.91 2.53 2.94 1.62 5.72 1.62 6.66 2.52 6.66 3.71 5.54 3.71 5.54 3.44 5.54 3.04 5.05 2.65 3.49 2.65 3.02 3.02 3.02 3.02 3.02 8.29 3.02 8.29 3.49 8.65 5.05 8.65 5.54 8.26 5.54 7.87 5.54 7.41 6.66 7.41 6.66 8.6 5.72 9.5"
        />
        <polygon
          className="element element--coach element--coach-outside"
          points="7.54 10.44 6.69 11.3 6.61 11.3 1.79 11.3 1.59 11.3 0.63 10.19 0 9.59 0 9.47 0 1.84 0 1.71 0.63 1.11 1.59 0 1.79 0 6.61 0 6.69 0 7.54 0.87 8.45 1.71 8.45 1.79 8.45 3.71 7.5 3.71 7.5 2.16 6.06 0.78 2.62 0.78 1.07 2.15 1.07 8.97 2.62 10.34 6.06 10.34 7.5 8.96 7.5 7.41 8.45 7.41 8.45 9.52 8.45 9.59 7.54 10.44"
        />
      </symbol>

      <symbol id="svg-star" viewBox="0 0 10.81 10.3">
        <path
          className="element element--star"
          d="M10.76,4.07a.13.13,0,0,0-.08-.24L7,3.58a.09.09,0,0,1-.06,0L5.53.09A.13.13,0,0,0,5.41,0a.13.13,0,0,0-.13.09L3.89,3.54a.1.1,0,0,1-.05,0L.13,3.83A.12.12,0,0,0,0,3.92a.14.14,0,0,0,0,.15L2.9,6.45a.07.07,0,0,1,0,.07L2,10.13a.11.11,0,0,0,0,.14.11.11,0,0,0,.08,0l.07,0,3.15-2h.07l3.15,2,.07,0a.11.11,0,0,0,.08,0,.13.13,0,0,0,.06-.14L7.89,6.52a.07.07,0,0,1,0-.07Z"
        />
      </symbol>

      <symbol id="svg-star-feed" viewBox="0 0 15.06 9.91">
        <path
          className="element element--star-feed element--star-feed-star"
          d="M5.55,6.58a.08.08,0,0,0-.11,0l-1.69,1L4.24,5.7a.08.08,0,0,0,0-.1L2.68,4.32l2-.13a.11.11,0,0,0,.09-.07L5.5,2.27l.74,1.85a.11.11,0,0,0,.09.07l2,.13L6.79,5.6a.1.1,0,0,0,0,.1l.49,1.93Z"
        />
        <path
          className="element element--star-feed element--star-feed-camera"
          d="M14.83,1.09a.8.8,0,0,0-.6-.25.91.91,0,0,0-.5.17L11,2.76l-.09.06V1.31a1.79,1.79,0,0,0,0-.23A1.3,1.3,0,0,0,9.62,0C9.2,0,8.79,0,8.38,0h-7A1.44,1.44,0,0,0,.72.15,1.16,1.16,0,0,0,0,1.22V4.88H0V5H0V8.68A1.16,1.16,0,0,0,.72,9.76a1.44,1.44,0,0,0,.68.15H9.62a1.3,1.3,0,0,0,1.24-1.07,2,2,0,0,0,0-.23V7.09l.09,0L13.73,8.9a.91.91,0,0,0,.5.17.8.8,0,0,0,.6-.25.84.84,0,0,0,.23-.63V1.71A.83.83,0,0,0,14.83,1.09ZM14.24,5V8.25l-.07,0-3.33-2-.49-.29-.07,0c-.12-.07-.22,0-.22.12V8.58a.43.43,0,0,1-.14.36.58.58,0,0,1-.41.15H1.38a1,1,0,0,1-.24,0,.43.43,0,0,1-.33-.43V1.29A.44.44,0,0,1,1.14.85a1,1,0,0,1,.24,0H9.51A.62.62,0,0,1,9.92,1a.45.45,0,0,1,.14.36V4c0,.14.1.2.22.13l.07,0,.49-.3,3.33-2,.07,0V5Z"
        />
      </symbol>

      <symbol id="svg-user-feed" viewBox="0 0 15.06 9.91">
        <path
          className="element element--user-feed element--user-feed-camera"
          d="M14.83,1.09a.8.8,0,0,0-.6-.25.91.91,0,0,0-.5.17L11,2.76l-.09.06V1.31a1.79,1.79,0,0,0,0-.23A1.3,1.3,0,0,0,9.62,0C9.2,0,8.79,0,8.38,0h-7A1.44,1.44,0,0,0,.72.15,1.16,1.16,0,0,0,0,1.22V4.88H0V5H0V8.68A1.16,1.16,0,0,0,.72,9.76a1.44,1.44,0,0,0,.68.15H9.62a1.3,1.3,0,0,0,1.24-1.07,2,2,0,0,0,0-.23V7.09l.09,0L13.73,8.9a.91.91,0,0,0,.5.17.8.8,0,0,0,.6-.25.84.84,0,0,0,.23-.63V1.71A.83.83,0,0,0,14.83,1.09ZM14.24,5V8.25l-.07,0-3.33-2-.49-.29-.07,0c-.12-.07-.22,0-.22.12V8.58a.43.43,0,0,1-.14.36.58.58,0,0,1-.41.15H1.38a1,1,0,0,1-.24,0,.43.43,0,0,1-.33-.43V1.29A.44.44,0,0,1,1.14.85a1,1,0,0,1,.24,0H9.51A.62.62,0,0,1,9.92,1a.45.45,0,0,1,.14.36V4c0,.14.1.2.22.13l.07,0,.49-.3,3.33-2,.07,0V5Z"
        />
        <path
          className="element element--user-feed element--user-feed-head"
          d="M5.44,5.38a1.34,1.34,0,1,0-1.35-1.3A1.34,1.34,0,0,0,5.44,5.38Z"
        />
        <path
          className="element element--user-feed element--user-feed-body"
          d="M5.43,6.08c-1,0-2.51.17-2.59,1.86H8C7.94,6.25,6.41,6.08,5.43,6.08Z"
        />
      </symbol>

      <symbol id="svg-video-play" viewBox="0 0 36 36">
        <circle
          className="element element--video-play-circle"
          cx="18"
          cy="18"
          r="16"
          fill="var(--icon-circle-fill, var(--brand-base))"
          stroke="var(--icon-circle-stroke, transparent)"
        />
        <path
          className="element element--video element--video-play-triangle"
          d="M13.6514 27.4054L28.0109 18.3042L13.6514 9.20312V27.4054Z"
          fill="var(--icon-triangle-fill, var(--brand-black))"
          stroke="var(--icon-triangle-stroke, transparent)"
        />
      </symbol>

      <symbol id="svg-reset-filters" viewBox="0 0 30 28">
        <path
          className="element element--circle-arrow"
          d="M0.666666 1.34146e-05H2.20617C2.58055 1.34146e-05 2.88155 0.308235 2.87267 0.682568L2.74228 6.15856C5.21805 2.43523 9.45622 -0.0149866 14.2663 6.89926e-05C21.8433 0.0237912 28.0118 6.22273 28 13.7998C27.9881 21.3989 21.8242 27.5555 14.2222 27.5555C10.6708 27.5555 7.43322 26.2118 4.99011 24.0051C4.70605 23.7486 4.69266 23.3073 4.96333 23.0366L6.05989 21.9401C6.30977 21.6902 6.71283 21.6742 6.97661 21.9094C8.90072 23.6248 11.4386 24.6667 14.2222 24.6667C20.2401 24.6667 25.1111 19.7966 25.1111 13.7778C25.1111 7.7599 20.241 2.8889 14.2222 2.8889C9.80305 2.8889 6.00328 5.51568 4.29583 9.29456L11.3174 9.1274C11.6917 9.11851 12 9.41945 12 9.79389V11.3333C12 11.7015 11.7015 12 11.3333 12H0.666666C0.2985 12 0 11.7015 0 11.3333V0.66668C0 0.298513 0.2985 1.34146e-05 0.666666 1.34146e-05Z"
          fill="var(--icon-element-fill, var(--brand-base))"
        />
      </symbol>

      <symbol id="svg-video-segment-brief" viewBox="0 0 17 19">
        <path
          className="element element--video-segment-brief-clock"
          d="M14.35,5.14,15,4.5a.42.42,0,0,0,0-.6l-.23-.23a.43.43,0,0,0-.6,0l-.64.65a8.14,8.14,0,0,0-4.67-2h0a.06.06,0,0,1-.06-.06V1.23a.06.06,0,0,1,.06-.06h.81a.45.45,0,0,0,.45-.45V.45A.45.45,0,0,0,9.62,0H6.73a.45.45,0,0,0-.45.45V.72a.45.45,0,0,0,.45.45h.81a.06.06,0,0,1,.06.06V2.29a.06.06,0,0,1-.06.06h0a8.18,8.18,0,1,0,6.85,2.79ZM8.17,17.5a7,7,0,1,1,7-7A7,7,0,0,1,8.17,17.5Z"
        />
        <path
          className="element element--video-segment-brief-zero"
          d="M6.59,8.46a1.57,1.57,0,0,1,.46-1.28,1.67,1.67,0,0,1,1.06-.35A1.47,1.47,0,0,1,9.67,8.42l0,4a1.46,1.46,0,0,1-.47,1.26A1.6,1.6,0,0,1,8.19,14a1.57,1.57,0,0,1-1-.31,1.51,1.51,0,0,1-.54-1.29Zm2.08.11A1,1,0,0,0,8.54,8a.52.52,0,0,0-.42-.2A.5.5,0,0,0,7.7,8a1,1,0,0,0-.11.57l0,3.69a1,1,0,0,0,.13.56.47.47,0,0,0,.42.21.48.48,0,0,0,.42-.22,1,1,0,0,0,.11-.56Z"
        />
      </symbol>

      <symbol id="svg-video-segment-warmup" viewBox="0 0 17 17">
        <path
          className="element element--video-segment-warmup-cirle"
          d="M8.17,16.35a8.18,8.18,0,1,1,8.18-8.18A8.19,8.19,0,0,1,8.17,16.35Zm0-15.19a7,7,0,1,0,7,7A7,7,0,0,0,8.17,1.16Z"
        />
        <path
          className="element element--video-segment-warmup-arrow"
          d="M11.21,8,8.37,4.91a.29.29,0,0,0-.39,0L5.14,8c-.12.14,0,.34.2.34H7.1v3a.25.25,0,0,0,.25.25H9a.25.25,0,0,0,.25-.25v-3H11C11.22,8.31,11.33,8.11,11.21,8Z"
        />
      </symbol>

      <symbol id="svg-video-segment-skills" viewBox="0 0 17 17">
        <path
          className="element element--video-segment-skills-square"
          d="M11,8.78v2.14H5.48V5.42h4.64l.82-.79H5.08a.39.39,0,0,0-.4.39v6.3a.39.39,0,0,0,.4.4h6.3a.39.39,0,0,0,.4-.4V8Z"
        />
        <polygon
          className="element element--video-segment-skills-check-mark"
          points="8.46 9.7 6.73 7.92 7.3 7.36 8.48 8.57 12.27 4.87 12.82 5.44 8.46 9.7"
        />
        <path
          className="element element--video-segment-skills-circle"
          d="M8.17,16.35a8.18,8.18,0,1,1,8.18-8.18A8.19,8.19,0,0,1,8.17,16.35Zm0-15.19a7,7,0,1,0,7,7A7,7,0,0,0,8.17,1.16Z"
        />
      </symbol>

      <symbol id="svg-video-segment-main" viewBox="0 0 19.5 19.5">
        <path
          className="element element--video-segment-main-circle"
          d="M9.64706 19.1765C4.35294 19.1765 0 14.9412 0 9.64706C0 4.35294 4.35294 0 9.64706 0C14.9412 0 19.2941 4.35294 19.2941 9.64706C19.2941 14.9412 14.9412 19.1765 9.64706 19.1765ZM9.64706 1.41176C5.05882 1.41176 1.41176 5.05882 1.41176 9.64706C1.41176 14.2353 5.05882 17.8824 9.64706 17.8824C14.2353 17.8824 17.8824 14.2353 17.8824 9.64706C17.8824 5.05882 14.1176 1.41176 9.64706 1.41176Z"
        />
        <path
          className="element element--video-segment-main-db-handle"
          d="M8.35274 11.4116L7.7645 10.8234C7.64686 10.8234 7.64686 10.7057 7.7645 10.5881L10.588 7.76457C10.7057 7.64692 10.8233 7.64692 10.8233 7.76457L11.4116 8.3528C11.5292 8.47045 11.5292 8.5881 11.4116 8.5881L8.58803 11.4116C8.58803 11.5293 8.47039 11.5293 8.35274 11.4116Z"
        />
        <path
          className="element element--video-segment-main-db-top-2"
          d="M9.88218 5.41164L10.4704 4.8234C10.5881 4.70576 10.7057 4.70576 10.7057 4.8234L14.3528 8.47046C14.4704 8.58811 14.4704 8.70576 14.3528 8.70576L13.7645 9.41164C13.7645 9.41164 13.6469 9.41164 13.5292 9.41164L9.88218 5.64693C9.76453 5.64693 9.76453 5.52929 9.88218 5.41164Z"
        />
        <path
          className="element element--video-segment-main-db-top-1"
          d="M11.8822 4.94112L12.2351 4.58817C12.3528 4.47053 12.4704 4.47053 12.4704 4.58817L14.7057 6.82347C14.8234 6.94112 14.8234 7.05876 14.7057 7.05876L14.3528 7.4117C14.2351 7.52935 14.1175 7.52935 14.1175 7.4117L11.8822 5.17641C11.7645 5.05876 11.7645 4.94112 11.8822 4.94112Z"
        />
        <path
          className="element element--video-segment-main-db-bottom-2"
          d="M7.41177 14.3528L7.05882 14.7058C6.94118 14.8234 6.82353 14.8234 6.82353 14.7058L4.58824 12.4705C4.47059 12.3528 4.47059 12.2352 4.58824 12.2352L4.94118 11.8822C5.05882 11.7646 5.17647 11.7646 5.17647 11.8822L7.41177 14.1175C7.41177 14.1175 7.41177 14.2352 7.41177 14.3528Z"
        />
        <path
          className="element element--video-segment-main-db-bottom-1"
          d="M9.41158 13.7647L8.82334 14.3529C8.7057 14.4705 8.58805 14.4705 8.58805 14.3529L4.82334 10.7058C4.7057 10.7058 4.7057 10.5882 4.82334 10.4705L5.41158 9.8823C5.52922 9.76466 5.64687 9.76466 5.64687 9.8823L9.41158 13.5294C9.41158 13.647 9.41158 13.7647 9.41158 13.7647Z"
        />
      </symbol>
      <symbol id="svg-streak" viewBox="0 0 19.5 19.5">
        <g id="Circle">
          <path
            d="M9.64706,19.1765 C4.35294,19.1765 0,14.9412 0,9.64706 C0,4.35294 4.35294,0 9.64706,0 C14.9412,0 19.2941,4.35294 19.2941,9.64706 C19.2941,14.9412 14.9412,19.1765 9.64706,19.1765 Z M9.64706,1.41176 C5.05882,1.41176 1.41176,5.05882 1.41176,9.64706 C1.41176,14.2353 5.05882,17.8824 9.64706,17.8824 C14.2353,17.8824 17.8824,14.2353 17.8824,9.64706 C17.8824,5.05882 14.1176,1.41176 9.64706,1.41176 Z"
            id="Shape"
          />
        </g>
        <g id="Streak" transform="translate(6.000000, 4.000000)">
          <path
            d="M3.35206497,0.0250116009 C3.50851508,0.132459397 3.64965197,0.227401392 3.78925754,0.324895592 C4.42424594,0.769489559 5.04136891,1.23603248 5.56303944,1.81538283 C6.0137587,2.31561485 6.35447796,2.875058 6.47060325,3.55062645 C6.5612065,4.07918794 6.51092807,4.59958237 6.37464037,5.11436195 C6.20032483,5.77385151 5.91447796,6.38663573 5.58805104,6.98232019 C5.55793503,7.03719258 5.52781903,7.09206497 5.49770302,7.14693735 C5.50433875,7.1599536 5.51097448,7.17271462 5.51761021,7.18573086 C5.66538283,7.1387703 5.81953596,7.10635731 5.95965197,7.0425522 C6.37081206,6.85470998 6.71178654,6.56452436 7.04561485,6.26642691 C7.16990719,6.15540603 7.29011601,6.0400464 7.42487239,5.91524362 C7.53767981,6.222529 7.6262413,6.52241299 7.65712297,6.83480278 C7.85083527,8.79795824 6.46677494,10.6067053 4.51918794,10.9402784 C2.41106729,11.3011601 0.435150812,9.92781903 0.078863109,7.81689095 C-0.150835267,6.45605568 0.273085847,5.29174014 1.27074246,4.33645012 C1.63341067,3.98909513 2.0162413,3.65832947 2.34573086,3.28136891 C3.11292343,2.40443155 3.43296984,1.36772622 3.35180974,0.206218097 C3.34900232,0.166403712 3.347471,0.126589327 3.3461949,0.086774942 C3.34593968,0.0750348028 3.34849188,0.0632946636 3.35257541,0.0255220418 L3.35206497,0.0250116009 Z M4.21904872,1.92921114 C4.20475638,1.96647332 4.19301624,1.99097448 4.18561485,2.01675174 C4.01640371,2.60146172 3.7412761,3.13283063 3.38396752,3.62412993 C2.98403712,4.17387471 2.47895592,4.62025522 1.98102088,5.07556845 C1.23016241,5.76211137 0.923642691,6.62322506 1.08136891,7.62419954 C1.28911833,8.94241299 2.3699768,9.86222738 3.61545244,9.96814385 C4.76828306,10.0661485 5.95352668,9.43039443 6.44814385,8.23570766 C6.49178654,8.13055684 6.52445476,8.02106729 6.55584687,7.93148492 C6.28429234,8.01213457 6.01988399,8.1137123 5.74577726,8.16705336 C5.16948956,8.27909513 4.67589327,7.99426914 4.5112761,7.47719258 C4.41914153,7.18777262 4.45640371,6.91136891 4.60519722,6.64721578 C4.89308585,6.1362645 5.16693735,5.61867749 5.34712297,5.05770302 C5.49055684,4.61132251 5.56406032,4.1575406 5.4624826,3.69150812 C5.29684455,2.93299304 4.77696056,2.42382831 4.21904872,1.92895592 L4.21904872,1.92921114 Z"
            id="Shape"
          />
        </g>
      </symbol>

      <symbol id="svg-video-segment-end" viewBox="0 0 17 19">
        <path
          className="element element--video-segment-end-clock"
          d="M14.35,5.14,15,4.5a.42.42,0,0,0,0-.6l-.23-.23a.43.43,0,0,0-.6,0l-.64.65a8.14,8.14,0,0,0-4.67-2h0a.06.06,0,0,1-.06-.06V1.23a.06.06,0,0,1,.06-.06h.81a.45.45,0,0,0,.45-.45V.45A.45.45,0,0,0,9.62,0H6.73a.45.45,0,0,0-.45.45V.72a.45.45,0,0,0,.45.45h.81a.06.06,0,0,1,.06.06V2.29a.06.06,0,0,1-.06.06h0a8.18,8.18,0,1,0,6.85,2.79ZM8.17,17.5a7,7,0,1,1,7-7A7,7,0,0,1,8.17,17.5Z"
        />
        <path
          className="element element--video-segment-end-six"
          d="M7.18,6.94,6,10a.9.9,0,0,1,.53-.12,1,1,0,0,1,.77.35,1.35,1.35,0,0,1,.31.91l0,1.37a1.54,1.54,0,0,1-.39,1.15,1.6,1.6,0,0,1-1.13.41,1.43,1.43,0,0,1-1-.38,1.45,1.45,0,0,1-.47-1.16v-1a3.3,3.3,0,0,1,.34-1.42L6.11,7Zm-.59,4.31a.58.58,0,0,0-.12-.4.45.45,0,0,0-.36-.17.55.55,0,0,0-.39.17.62.62,0,0,0-.17.45v1.19a.65.65,0,0,0,.12.41.47.47,0,0,0,.38.17.55.55,0,0,0,.42-.17.74.74,0,0,0,.12-.51Z"
        />
        <path
          className="element element--video-segment-end-zero"
          d="M8.68,8.43a1.51,1.51,0,0,1,.46-1.27,1.66,1.66,0,0,1,1-.35,1.54,1.54,0,0,1,1.17.44,1.5,1.5,0,0,1,.39,1.15l.05,4a1.5,1.5,0,0,1-.48,1.27,1.55,1.55,0,0,1-1,.36,1.51,1.51,0,0,1-1-.31,1.48,1.48,0,0,1-.54-1.28Zm2.08.12A1.08,1.08,0,0,0,10.63,8a.47.47,0,0,0-.42-.2A.5.5,0,0,0,9.79,8a.89.89,0,0,0-.11.56l0,3.7a.91.91,0,0,0,.13.56.45.45,0,0,0,.42.2.43.43,0,0,0,.41-.21,1,1,0,0,0,.12-.56Z"
        />
      </symbol>

      <symbol id="svg-video-segment-cooldown" viewBox="0 0 17 17">
        <path
          className="element element--video-segment-cooldown-circle"
          d="M8.17,0A8.18,8.18,0,1,1,0,8.17,8.19,8.19,0,0,1,8.17,0Zm0,15.19a7,7,0,1,0-7-7A7,7,0,0,0,8.17,15.19Z"
        />
        <path
          className="element element--video-segment-cooldown-arrow"
          d="M11,8H9.25v-3A.25.25,0,0,0,9,4.83H7.35a.25.25,0,0,0-.25.25V8H5.34c-.21,0-.32.2-.2.34L8,11.44a.29.29,0,0,0,.39,0l2.84-3.06C11.33,8.24,11.22,8,11,8Z"
        />
      </symbol>

      <symbol id="svg-video-segment-scrubber" viewBox="0 0 16 16">
        <circle
          className="element element--video-segment-scrubber-circle"
          cx="8"
          cy="8"
          r="8"
          fill="var(--brand-base)"
        />
        <path
          className="element element--video-segment-scrubber-thumb"
          d="M11.6641 5.98438C11.7695 5.98438 11.875 5.89648 11.875 5.77344V5.21094C11.875 5.10547 11.7695 5 11.6641 5H4.21094C4.08789 5 4 5.10547 4 5.21094V5.77344C4 5.89648 4.08789 5.98438 4.21094 5.98438H11.6641ZM11.6641 8.79688C11.7695 8.79688 11.875 8.70898 11.875 8.58594V8.02344C11.875 7.91797 11.7695 7.8125 11.6641 7.8125H4.21094C4.08789 7.8125 4 7.91797 4 8.02344V8.58594C4 8.70898 4.08789 8.79688 4.21094 8.79688H11.6641ZM11.6641 11.6094C11.7695 11.6094 11.875 11.5215 11.875 11.3984V10.8359C11.875 10.7305 11.7695 10.625 11.6641 10.625H4.21094C4.08789 10.625 4 10.7305 4 10.8359V11.3984C4 11.5215 4.08789 11.6094 4.21094 11.6094H11.6641Z"
        />
      </symbol>

      <symbol id="svg-l1" viewBox="0 0 22 17">
        <path
          className="element element--l1-rect"
          d="M21.0023 0H0.997732C0.4467 0 0 0.455213 0 1.01675V15.9833C0 16.5448 0.4467 17 0.997732 17H21.0023C21.5533 17 22 16.5448 22 15.9833V1.01675C22 0.455213 21.5533 0 21.0023 0Z"
          fill="var(--icon-color, var(--text-dark))"
        />
        <path
          className="element element--l1-level"
          d="M7.14374 11.0423H10.4263V12.5268H5.24805V4.53516H7.14374V11.0423Z"
          fill="var(--icon-fg, var(--white))"
        />
        <path
          className="element element--l1-number"
          d="M15.3651 12.5253H13.5193V6.53671L11.7134 7.11625V5.65214L15.1955 4.42188H15.3651V12.5253Z"
          fill="var(--icon-fg, var(--white))"
        />
      </symbol>

      <symbol id="svg-l2" viewBox="0 0 22 17">
        <path
          className="element element--l2-rect"
          d="M21.0023 0H0.997732C0.4467 0 0 0.455213 0 1.01675V15.9833C0 16.5448 0.4467 17 0.997732 17H21.0023C21.5533 17 22 16.5448 22 15.9833V1.01675C22 0.455213 21.5533 0 21.0023 0Z"
          fill="var(--icon-color, var(--text-dark))"
        />
        <path
          className="element element--l2-level"
          d="M7.14374 11.0423H10.4263V12.5268H5.24805V4.53516H7.14374V11.0423Z"
          fill="var(--icon-fg, var(--white))"
        />
        <path
          className="element element--l2-number"
          d="M16.9017 12.5275H11.4341V11.3176L13.9683 8.62317C14.4548 8.17012 14.781 7.56612 14.8962 6.90487C14.9177 6.61605 14.8323 6.32949 14.6568 6.10164C14.5789 6.0081 14.4805 5.93452 14.3696 5.88684C14.2587 5.83915 14.1383 5.8187 14.0182 5.82712C13.8854 5.82492 13.7543 5.85741 13.6372 5.92148C13.5202 5.98555 13.4213 6.07907 13.3498 6.19315C13.1664 6.45287 13.0749 6.76832 13.0903 7.08789H11.2745C11.2714 6.60903 11.3955 6.13825 11.6337 5.72545C11.8651 5.29711 12.2111 4.94453 12.6314 4.7087C13.0706 4.47116 13.5609 4.34886 14.0581 4.35284C14.7731 4.29571 15.4836 4.5093 16.0536 4.95272C16.2884 5.16741 16.4724 5.43359 16.5916 5.73123C16.7109 6.02887 16.7623 6.3502 16.742 6.67102C16.7408 6.98404 16.6833 7.29419 16.5724 7.58609C16.4355 7.92146 16.2542 8.23617 16.0337 8.5215C15.6731 8.98428 15.2832 9.42242 14.8663 9.8331L13.8686 11.0227H16.9516L16.9017 12.5275Z"
          fill="var(--icon-fg, var(--white))"
        />
      </symbol>
      <symbol id="svg-l3" viewBox="0 0 22 17">
        <path
          className="element element--l3-rect"
          d="M21.0023 0H0.997732C0.4467 0 0 0.455213 0 1.01675V15.9833C0 16.5448 0.4467 17 0.997732 17H21.0023C21.5533 17 22 16.5448 22 15.9833V1.01675C22 0.455213 21.5533 0 21.0023 0Z"
          fill="var(--icon-color, var(--text-dark))"
        />
        <path
          className="element element--l3-level"
          d="M7.14374 11.0423H10.4263V12.5268H5.24805V4.53516H7.14374V11.0423Z"
          fill="var(--icon-fg, var(--white))"
        />
        <path
          className="element element--l3-number"
          d="M13.1301 7.71567H13.9682C14.1035 7.73409 14.2411 7.72048 14.3704 7.67589C14.4997 7.63131 14.6172 7.55695 14.7137 7.4586C14.8102 7.36025 14.8832 7.24055 14.9269 7.1088C14.9707 6.97705 14.984 6.83679 14.966 6.69893C14.9719 6.47753 14.8932 6.26244 14.7465 6.09905C14.6553 6.01391 14.5482 5.94841 14.4317 5.90647C14.3151 5.86454 14.1914 5.84704 14.068 5.85503C13.8446 5.8455 13.6252 5.91762 13.4494 6.05838C13.3658 6.11767 13.2977 6.19678 13.2507 6.28887C13.2038 6.38097 13.1795 6.48331 13.18 6.58709H11.3941C11.3869 6.19005 11.5059 5.80128 11.7333 5.47883C11.9881 5.13331 12.3342 4.86883 12.731 4.71627C13.1198 4.53425 13.5406 4.43397 13.9682 4.42141C14.6805 4.38793 15.3833 4.59919 15.9637 5.0213C16.2046 5.22011 16.3968 5.47339 16.525 5.76107C16.6531 6.04874 16.7137 6.36289 16.702 6.67859C16.6996 7.02073 16.5952 7.35403 16.4027 7.63433C16.1755 7.95818 15.8646 8.21156 15.5047 8.36639C15.8977 8.48995 16.2453 8.73079 16.5025 9.05778C16.7554 9.37015 16.8962 9.7611 16.9016 10.166C16.911 10.492 16.8435 10.8154 16.7046 11.1092C16.5657 11.4029 16.3595 11.6583 16.1034 11.8538C15.4963 12.303 14.7576 12.5288 14.0081 12.4944C13.5209 12.497 13.0385 12.3966 12.5914 12.1995C12.1884 12.0278 11.8421 11.742 11.5936 11.376C11.3731 11.0241 11.2586 10.614 11.2644 10.1965H13.0603C13.0619 10.315 13.0886 10.4318 13.1385 10.5388C13.1884 10.6459 13.2604 10.7407 13.3496 10.8168C13.5427 10.996 13.797 11.0909 14.058 11.0811C14.1955 11.0929 14.3338 11.0754 14.4643 11.0299C14.5949 10.9844 14.7147 10.9118 14.8163 10.8168C14.9077 10.73 14.98 10.6244 15.0281 10.5069C15.0763 10.3894 15.0993 10.2628 15.0957 10.1355C15.1137 9.9817 15.097 9.8257 15.0469 9.67944C14.9967 9.53318 14.9145 9.40053 14.8063 9.29163C14.5637 9.11397 14.2651 9.03427 13.9682 9.06795H13.0902L13.1301 7.71567Z"
          fill="var(--icon-fg, var(--white))"
        />
      </symbol>
      <symbol id="svg-l4" viewBox="0 0 22 17">
        <path
          className="element element--l4-rect"
          d="M21.0023 0H0.997732C0.4467 0 0 0.455213 0 1.01675V15.9833C0 16.5448 0.4467 17 0.997732 17H21.0023C21.5533 17 22 16.5448 22 15.9833V1.01675C22 0.455213 21.5533 0 21.0023 0Z"
          fill="var(--icon-color, var(--text-dark))"
        />
        <path
          className="element element--l4-level"
          d="M7.14374 11.0423H10.4263V12.5268H5.24805V4.53516H7.14374V11.0423Z"
          fill="var(--icon-fg, var(--white))"
        />
        <path
          className="element element--l4-number"
          d="M16.5225 9.22182H17.3905V10.7774H16.5225V12.6178H14.527V10.7774H11.2345L11.1147 9.54718L14.527 3.95508H16.5225V9.22182ZM13.0204 9.22182H14.5569V6.55795L14.4272 6.7613L13.0204 9.22182Z"
          fill="var(--icon-fg, var(--white))"
        />
      </symbol>
      <symbol id="svg-demo-video" viewBox="0 0 16 10">
        <path
          className="element element--outside-camera"
          d="M8.76,0H1.22A1.25,1.25,0,0,0,0,1.25V8.78A1.22,1.22,0,0,0,1.22,10H8.76a1.94,1.94,0,0,0,1.37-.47,1.61,1.61,0,0,0,.28-.39,1.58,1.58,0,0,0,.18-.74l.09,0,1.55.67a3.49,3.49,0,0,0,.53.2,3.54,3.54,0,0,0,.63.13A1.75,1.75,0,0,0,15,8.39V1.65a1.14,1.14,0,0,0-.23-.54,1.24,1.24,0,0,0-.53-.35A3,3,0,0,0,13,.71a8.7,8.7,0,0,0-1.26.51c-.29.13-.72.31-1.27.51A1.68,1.68,0,0,0,10,.46,1.74,1.74,0,0,0,8.76,0Z"
          fill="var(--icon-element-outline, var(--brand-gray-3))"
        />
        <path
          className="element element--inside-camera"
          d="M13.8,1.93a.86.86,0,0,0-.72-.18L9.67,3.1V2a1,1,0,0,0-.32-.71A1.21,1.21,0,0,0,8.59,1H2.06A1,1,0,0,0,1,2v6A1,1,0,0,0,2.06,9H8.59a1,1,0,0,0,1.08-1V6.82l3.17,1.45a.78.78,0,0,0,.21.09h0a.81.81,0,0,0,.75-.16.69.69,0,0,0,.2-.45V2.36A.66.66,0,0,0,13.8,1.93Z"
          fill="var(--icon-element-fill, var(--white))"
        />
      </symbol>
      <symbol id="svg-upload-image" viewBox="0 0 80 80">
        <circle
          className="element element--bg"
          cx="40"
          cy="40"
          r="40"
          fill="var(--white)"
        />
        <path
          className="element element--head"
          d="M40.05,47.49A15.76,15.76,0,1,0,24.24,32.2,15.7,15.7,0,0,0,40.05,47.49Z"
          fill="var(--brand-gray-6)"
        />
        <path
          className="element element--body"
          d="M40,80A39.88,39.88,0,0,0,69.27,67.25C64.84,53.9,50,52.39,40,52.39S15.16,53.9,10.73,67.25A39.88,39.88,0,0,0,40,80Z"
          fill="var(--brand-gray-6)"
        />
        <path
          className="element element--plus-circle"
          d="M61.53,61A9.58,9.58,0,1,0,52,51.68,9.53,9.53,0,0,0,61.53,61Z"
          fill="var(--brand-base)"
        />
        <path
          className="element element--plus-sign"
          d="M62.73,50.07v-4a.1.1,0,0,0-.09-.1H60.37a.1.1,0,0,0-.1.1v4a.09.09,0,0,1-.1.09H56.23a.1.1,0,0,0-.1.1v2.27a.1.1,0,0,0,.1.1h3.94a.09.09,0,0,1,.1.09v4a.09.09,0,0,0,.1.1h2.27a.09.09,0,0,0,.09-.1v-4a.1.1,0,0,1,.1-.09h4a.11.11,0,0,0,.1-.1V50.26a.11.11,0,0,0-.1-.1h-4A.1.1,0,0,1,62.73,50.07Z"
          fill="var(--white)"
        />
      </symbol>
      <symbol id="svg-hide-kit-accessories" viewBox="0 0 34 34">
        <line
          className="element element--close1"
          x1="11.24"
          y1="11.3"
          x2="22.53"
          y2="22.59"
          stroke="var(--icon-line-stroke, var(--brand-base))"
          strokeWidth="var(--icon-line-width, 1)"
        />
        <line
          className="element element--close2"
          x1="22.53"
          y1="11.3"
          x2="11.24"
          y2="22.58"
          stroke="var(--icon-line-stroke, var(--brand-base))"
          strokeWidth="var(--icon-line-width, 1)"
        />
        <circle
          className="element element--circle"
          cx="17"
          cy="17"
          r="16.5"
          fill="var(--icon-circle-fill, transparent)"
          stroke="var(--icon-circle-stroke, var(--brand-base))"
          strokeWidth="var(--icon-stroke-width, 1)"
        />
      </symbol>
      <symbol id="svg-see-kit-accessories" viewBox="0 0 35 35">
        <polyline
          className="element element--caret"
          points="14.68 11.86 20.32 17.5 14.68 23.14"
          fill="var(--icon-circle-fill, transparent)"
          stroke="var(--icon-circle-stroke, var(--brand-base))"
          strokeWidth="var(--icon-stroke-width, 1)"
        />
        <circle
          className="element element--circle"
          cx="17"
          cy="17"
          r="16.5"
          fill="var(--icon-circle-fill, transparent)"
          stroke="var(--icon-circle-stroke, var(--brand-base))"
          strokeWidth="var(--icon-stroke-width, 1)"
        />
      </symbol>
      <symbol id="svg-video-scrub-back15" viewBox="0 0 36 36">
        <path
          className="element element--circle"
          d="M18,0A18,18,0,1,0,36,18,18,18,0,0,0,18,0Z"
          fill="var(--icon-circle-fill, transparent)"
        />
        <path
          className="element element--one"
          d="M14.64,15.21l-1.52,1.1V14.85l1.57-1.15h1.36v9.86H14.64Z"
        />
        <path
          className="element element--five"
          d="M22.19,13.7V15H19.46v2.59a1.56,1.56,0,0,1,.45-.28,1.72,1.72,0,0,1,.66-.13,1.68,1.68,0,0,1,1.21.51,1.87,1.87,0,0,1,.41,1.21v2.66a2.12,2.12,0,0,1-.54,1.55,2,2,0,0,1-1.57.62,2.16,2.16,0,0,1-1.52-.54,2,2,0,0,1-.65-1.54v-.44h1.41v.27a1,1,0,0,0,.14.6.71.71,0,0,0,.57.27.67.67,0,0,0,.57-.24,1,1,0,0,0,.18-.63V19.31a.87.87,0,0,0-.17-.62.56.56,0,0,0-.49-.22.69.69,0,0,0-.55.23,1.32,1.32,0,0,0-.23.35H18.05V13.7Z"
        />
        <path
          className="element element--arrow"
          d="M11.32,8.3a.39.39,0,0,0-.23.07A12.27,12.27,0,0,0,16.67,30.7a12.15,12.15,0,0,0,9.53-3.07A12.27,12.27,0,0,0,20.54,6.5h-.07V3.42a.18.18,0,0,0-.12-.16.28.28,0,0,0-.3,0L15.52,7.11a.16.16,0,0,0,0,.26l4.53,3.82a.3.3,0,0,0,.3,0s.12-.06.12-.16V8.55l.11,0A10.26,10.26,0,0,1,18,28.77h-.45a10.27,10.27,0,0,1-5.39-18.7.42.42,0,0,0,.13-.57l-.61-1a.45.45,0,0,0-.28-.19Z"
        />
      </symbol>
      <symbol id="svg-video-scrub-forward15" viewBox="0 0 36 36">
        <path
          className="element element--circle"
          d="M18,0A18,18,0,1,0,36,18,18,18,0,0,0,18,0Z"
          fill="var(--icon-circle-fill, transparent)"
        />
        <path
          className="element element--one"
          d="M15,14.59l-1.52,1.1V14.24l1.58-1.15h1.35V23H15Z"
        />
        <path
          className="element element--five"
          d="M22.59,13.09v1.26H19.86V17a1.51,1.51,0,0,1,.45-.29,1.92,1.92,0,0,1,.66-.12,1.7,1.7,0,0,1,1.22.5,1.88,1.88,0,0,1,.4,1.22v2.66a2.18,2.18,0,0,1-.53,1.55,2,2,0,0,1-1.58.62A2.19,2.19,0,0,1,19,22.55,2,2,0,0,1,18.31,21v-.43h1.41v.26a1,1,0,0,0,.14.61.72.72,0,0,0,.58.27.66.66,0,0,0,.56-.24,1,1,0,0,0,.19-.64V18.69a1,1,0,0,0-.17-.62.6.6,0,0,0-.5-.21.66.66,0,0,0-.55.23,1.38,1.38,0,0,0-.22.35h-1.3V13.09Z"
        />
        <path
          className="element element--arrow"
          d="M24.6,7.69a.45.45,0,0,0-.28.2l-.62,1a.44.44,0,0,0,.13.58,10.26,10.26,0,0,1-5.38,18.69H18A10.27,10.27,0,0,1,7.74,17.89,10.27,10.27,0,0,1,15.42,8l.12,0v2.51a.19.19,0,0,0,.12.16.28.28,0,0,0,.3,0l4.53-3.82a.16.16,0,0,0,.06-.12.16.16,0,0,0-.06-.13L16,2.68a.28.28,0,0,0-.3,0,.19.19,0,0,0-.12.17V5.87l-.08,0A12.26,12.26,0,0,0,9.8,27a12.12,12.12,0,0,0,9.53,3.08A12.27,12.27,0,0,0,24.91,7.75a.41.41,0,0,0-.22-.07Z"
        />
      </symbol>
      <symbol id="svg-video-scrub-forward30" viewBox="0 0 36 36">
        <path
          className="element element--circle"
          d="M18,0A18,18,0,1,0,36,18,18,18,0,0,0,18,0Z"
          fill="var(--icon-circle-fill, transparent)"
        />
        <path
          className="element element--three"
          d="M13,15.19a1.91,1.91,0,0,1,.68-1.55,2.1,2.1,0,0,1,1.42-.48,2.39,2.39,0,0,1,1.47.45,2,2,0,0,1,.71,1.58v1.4A1.4,1.4,0,0,1,16.39,18a1.67,1.67,0,0,1,.85,1.75v.94a2.31,2.31,0,0,1-.57,1.88,2.16,2.16,0,0,1-1.64.57,2.06,2.06,0,0,1-1.52-.52A1.9,1.9,0,0,1,13,21.23v-1h1.39v.64a1.17,1.17,0,0,0,.18.66.71.71,0,0,0,.58.25c.35,0,.74-.16.74-.83V19.52a1,1,0,0,0-.2-.69c-.18-.21-.37-.19-.61-.19h-.33V17.4H15a.79.79,0,0,0,.6-.21.93.93,0,0,0,.22-.64V15.32a.88.88,0,0,0-.22-.59.77.77,0,0,0-.56-.21.63.63,0,0,0-.51.25,1.1,1.1,0,0,0-.21.71V16H13Z"
        />
        <path
          className="element element--zero"
          d="M18.77,15.4a2.16,2.16,0,0,1,.66-1.76,2.33,2.33,0,0,1,1.48-.48,2.15,2.15,0,0,1,1.61.64A2.06,2.06,0,0,1,23,15.4v5.53a2.06,2.06,0,0,1-.68,1.75,2.21,2.21,0,0,1-1.45.48,2.16,2.16,0,0,1-1.42-.44,2.06,2.06,0,0,1-.72-1.79Zm2.89.19a1.35,1.35,0,0,0-.17-.77.69.69,0,0,0-.58-.3.72.72,0,0,0-.59.3,1.33,1.33,0,0,0-.16.77v5.14a1.38,1.38,0,0,0,.16.78.66.66,0,0,0,.59.29.63.63,0,0,0,.58-.29,1.46,1.46,0,0,0,.17-.78Z"
        />
        <path
          className="element element--arrow"
          d="M24.6,7.69a.45.45,0,0,0-.28.2l-.62,1a.44.44,0,0,0,.13.58,10.26,10.26,0,0,1-5.38,18.69H18A10.27,10.27,0,0,1,7.74,17.89,10.27,10.27,0,0,1,15.42,8l.12,0v2.51a.19.19,0,0,0,.12.16.28.28,0,0,0,.3,0l4.53-3.82a.16.16,0,0,0,.06-.12.16.16,0,0,0-.06-.13L16,2.68a.28.28,0,0,0-.3,0,.19.19,0,0,0-.12.17V5.87l-.08,0A12.26,12.26,0,0,0,9.8,27a12.12,12.12,0,0,0,9.53,3.08A12.27,12.27,0,0,0,24.91,7.75a.41.41,0,0,0-.22-.07Z"
        />
      </symbol>
      <symbol id="svg-video-full-screen" viewBox="0 0 36 36">
        <path
          className="element element--circle"
          d="M18,0A18,18,0,1,0,36,18,18,18,0,0,0,18,0Z"
          fill="var(--icon-circle-fill, transparent)"
        />
        <path
          className="element left-top-corner"
          d="M10.56,15.52H9.26a.35.35,0,0,1-.35-.35V8.7a.35.35,0,0,1,.35-.35h6.47a.35.35,0,0,1,.35.35V10a.35.35,0,0,1-.35.35H11.26a.35.35,0,0,0-.35.35v4.47A.35.35,0,0,1,10.56,15.52Z"
        />
        <path
          className="element right-top-corner"
          d="M27.07,15.52h-1.3a.35.35,0,0,1-.35-.35V10.7a.35.35,0,0,0-.35-.35H20.6a.35.35,0,0,1-.35-.35V8.7a.35.35,0,0,1,.35-.35h6.47a.35.35,0,0,1,.35.35v6.47A.35.35,0,0,1,27.07,15.52Z"
        />
        <path
          className="element left-bottom-corner"
          d="M15.73,26.86H9.26a.35.35,0,0,1-.35-.35V20a.35.35,0,0,1,.35-.35h1.3a.35.35,0,0,1,.35.35v4.47a.35.35,0,0,0,.35.35h4.47a.35.35,0,0,1,.35.35v1.3A.35.35,0,0,1,15.73,26.86Z"
        />
        <path
          className="element right-bottom-corner"
          d="M27.07,26.86H20.6a.35.35,0,0,1-.35-.35v-1.3a.35.35,0,0,1,.35-.35h4.47a.35.35,0,0,0,.35-.35V20a.35.35,0,0,1,.35-.35h1.3a.35.35,0,0,1,.35.35v6.47A.35.35,0,0,1,27.07,26.86Z"
        />
      </symbol>
      <symbol id="svg-video-sound" viewBox="0 0 36 36">
        <path
          className="element element--circle"
          d="M18,0A18,18,0,1,0,36,18,18,18,0,0,0,18,0Z"
          fill="var(--icon-circle-fill, transparent)"
        />
        <path
          className="element element--speaker"
          d="M7.76,16.12v3a.47.47,0,0,0,.46.47h2.63a.46.46,0,0,1,.29.11l6.16,5.4a.47.47,0,0,0,.76-.36V10.51a.47.47,0,0,0-.76-.36l-6.16,5.4a.45.45,0,0,1-.29.1H8.22A.47.47,0,0,0,7.76,16.12Z"
        />
        <path
          className="element element--wave-2"
          d="M21,9v1.57a.2.2,0,0,0,.11.18,7.95,7.95,0,0,1,0,14.41.2.2,0,0,0-.11.18v1.57a.21.21,0,0,0,.28.19,9.82,9.82,0,0,0,0-18.29A.21.21,0,0,0,21,9Z"
        />
        <path
          className="element element--wave-1"
          d="M23.45,17.94a6.24,6.24,0,0,0-2.1-4.65.2.2,0,0,0-.33.16v9a.2.2,0,0,0,.33.16A6.21,6.21,0,0,0,23.45,17.94Z"
        />
      </symbol>
      <symbol id="svg-video-pause" viewBox="0 0 36 36">
        <path
          className="element element--circle"
          d="M18,0A18,18,0,1,0,36,18,18,18,0,0,0,18,0Z"
          fill="var(--icon-circle-fill, transparent)"
        />
        <path
          className="element element--left-rect"
          d="M15.72,25.42H12.1a.29.29,0,0,1-.29-.3V11a.29.29,0,0,1,.29-.3h3.62A.29.29,0,0,1,16,11v14.1A.29.29,0,0,1,15.72,25.42Z"
        />
        <path
          className="element element--right-rect"
          d="M23.91,25.42H20.29a.29.29,0,0,1-.29-.3V11a.29.29,0,0,1,.29-.3h3.62a.29.29,0,0,1,.29.3v14.1A.29.29,0,0,1,23.91,25.42Z"
        />
      </symbol>
      <symbol id="svg-bookmark" viewBox="0 0 9 12">
        <path
          className="element element--tag"
          d="M8.5 11.2117L4.4708 7.94161L0.5 11.2117V0H8.5V11.2117Z"
        />
      </symbol>
      <symbol id="svg-rank" viewBox="0 0 9 11">
        <path
          className="element element--bottom-caret"
          d="M4.11,9.82,2.91,8.61l-.06,0H2.2a.1.1,0,0,0-.08.06.08.08,0,0,0,0,.1l2,2a.1.1,0,0,0,.14,0l2-2a.08.08,0,0,0,0-.1.09.09,0,0,0-.08-.06H5.52a.09.09,0,0,0-.07,0L4.25,9.82A.12.12,0,0,1,4.11,9.82Z"
        />
        <rect
          className="element element--bottom-line"
          x="0.06"
          y="6.3"
          width="8.24"
          height="0.61"
        />
        <rect
          className="element element--top-line"
          x="0.06"
          y="3.84"
          width="8.24"
          height="0.61"
        />
        <path
          className="element element--top-caret"
          d="M4.25.93l1.2,1.21.06,0h.65a.08.08,0,0,0,.08-.05.09.09,0,0,0,0-.11l-2-2a.1.1,0,0,0-.14,0l-2,2a.09.09,0,0,0,0,.11.09.09,0,0,0,.08.05h.65l.06,0L4.11.93A.12.12,0,0,1,4.25.93Z"
        />
      </symbol>
      <symbol id="svg-rpm-element" viewBox="0 0 21 18">
        <path
          className="element element--top-left"
          d="M9.6 1L2 8.6H9.6V1Z"
          fill="var(--icon-element-fill)"
        />
        <path
          className="element element--bottom-left"
          d="M9.6 10.0996H2L9.6 17.6996V10.0996Z"
          fill="var(--icon-element-fill)"
        />
        <path
          className="element element--bottom-right"
          d="M11.1001 10.0996V17.6996L18.7001 10.0996H11.1001Z"
          fill="var(--icon-element-fill)"
        />
        <path
          className="element element--top-right"
          d="M11.1001 8.6H18.7001L11.1001 1V8.6Z"
          fill="var(--icon-element-fill)"
        />
      </symbol>
      <symbol id="svg-app-icon" viewBox="0 0 48 48">
        <rect
          className="element element--bg-rect"
          width="48"
          height="48"
          fill="black"
        />
        <path
          className="element element--a"
          d="M36.58,11.72H7.77v6.09h24.7V21H7.77V32.34l3.58,3.93H40.23V16ZM32.47,30.2H15.57V27l16.9,0Z"
          fill="var(--icon-element-fill)"
        />
      </symbol>
      <symbol id="svg-group-image-placeholder" viewBox="0 0 80 80">
        <path
          className="element element--circle"
          d="M40 80C62.0914 80 80 62.0914 80 40C80 17.9086 62.0914 0 40 0C17.9086 0 0 17.9086 0 40C0 62.0914 17.9086 80 40 80Z"
          fill="var(--element-circle-fill, var(--white))"
        />
        <path
          className="element element--body"
          d="M17 61C19.6278 61.003 22.2278 60.3893 24.6369 59.1973C27.046 58.0053 29.2123 56.2608 31 54.0732C28.8811 46.8204 21.7831 46 17 46C12.2169 46 5.11889 46.8204 3 54.0732C4.78765 56.2608 6.95397 58.0053 9.36311 59.1973C11.7722 60.3893 14.3722 61.003 17 61V61Z"
          fill="var(--icon-element-fill, var(--brand-gray-6))"
        />
        <path
          className="element element--head"
          d="M14.613 42.8596C16.3891 42.8532 18.123 42.3173 19.5929 41.3204C21.0629 40.3234 22.2022 38.9108 22.8651 37.2631C23.5281 35.6153 23.6847 33.8073 23.3148 32.0701C22.9449 30.333 22.0654 28.7455 20.7886 27.5108C19.5119 26.2761 17.896 25.4501 16.1474 25.1385C14.3988 24.8269 12.597 25.0438 10.9724 25.7615C9.34772 26.4792 7.97398 27.665 7.02677 29.1675C6.07955 30.67 5.60189 32.4208 5.65485 34.1961C5.71565 36.5257 6.6882 38.7385 8.3634 40.3586C10.0386 41.9787 12.2826 42.8767 14.613 42.8596V42.8596Z"
          fill="var(--icon-element-fill, var(--brand-gray-6))"
        />
        <path
          className="element element--body_2"
          d="M61.5 61C64.2216 61.0032 66.9145 60.3486 69.4096 59.0771C71.9048 57.8057 74.1485 55.9449 76 53.6114C73.8054 45.875 66.4539 45 61.5 45C56.5461 45 49.1946 45.875 47 53.6114C48.8515 55.9449 51.0952 57.8057 53.5904 59.0771C56.0855 60.3486 58.7784 61.0032 61.5 61V61Z"
          fill="var(--icon-element-fill, var(--brand-gray-6))"
        />
        <path
          className="element element--head_2"
          d="M63.5281 42.6736C65.2952 42.6672 67.0201 42.134 68.4826 41.1422C69.945 40.1504 71.0785 38.745 71.7381 37.1057C72.3976 35.4663 72.5534 33.6676 72.1854 31.9393C71.8174 30.211 70.9424 28.6317 69.6722 27.4033C68.402 26.1748 66.7943 25.3531 65.0547 25.0431C63.315 24.7331 61.5225 24.9489 59.9061 25.6629C58.2898 26.3769 56.923 27.5567 55.9807 29.0515C55.0383 30.5463 54.5631 32.2881 54.6158 34.0544C54.6763 36.3721 55.6438 38.5735 57.3105 40.1854C58.9771 41.7972 61.2097 42.6906 63.5281 42.6736V42.6736Z"
          fill="var(--icon-element-fill, var(--brand-gray-6))"
        />
        <path
          className="element element--body_3"
          d="M40.0013 76L40 77L39.9987 76C35.4846 76.0058 31.0081 74.8256 26.8474 72.5199C22.8104 70.2828 19.1503 67.03 16.0914 62.9407C17.9549 56.2776 21.7878 52.5939 26.1429 50.5378C30.6489 48.4105 35.773 48 40 48C44.227 48 49.3511 48.4105 53.8571 50.5378C58.2122 52.5939 62.0451 56.2776 63.9086 62.9407C60.8497 67.03 57.1896 70.2828 53.1526 72.5199C48.9919 74.8256 44.5154 76.0058 40.0013 76Z"
          fill="var(--icon-element-fill, var(--brand-gray-6))"
          stroke="var(--icon-circle-stroke, var(--white))"
          strokeWidth="var(--icon-stroke-width, 2)"
        />
        <path
          className="element element--head_3"
          d="M39.6973 42.7041C42.353 42.6945 44.9454 41.8932 47.1434 40.4026C49.3413 38.912 51.0447 36.7998 52.036 34.336C53.0273 31.8723 53.2614 29.1689 52.7083 26.5714C52.1553 23.974 50.8402 21.6004 48.9312 19.7542C47.0222 17.908 44.606 16.673 41.9915 16.2071C39.377 15.7412 36.6829 16.0655 34.2537 17.1386C31.8245 18.2117 29.7704 19.9848 28.3541 22.2314C26.9378 24.4779 26.2236 27.0957 26.3028 29.7502C26.3937 33.2336 27.8479 36.5422 30.3527 38.9646C32.8575 41.387 36.2128 42.7297 39.6973 42.7041V42.7041Z"
          fill="var(--icon-element-fill, var(--brand-gray-6))"
        />
        <path
          className="element element--teal-circle"
          d="M61.5277 61.9997C63.6338 62.0095 65.6948 61.3904 67.447 60.2218C69.1999 59.0528 70.5637 57.387 71.3639 55.4379C72.1641 53.4888 72.3641 51.3452 71.9383 49.2817C71.5125 47.2183 70.4803 45.3289 68.974 43.8558C67.4676 42.3826 65.5558 41.3928 63.4833 41.0131C61.4109 40.6334 59.2723 40.8811 57.3415 41.7245C55.4107 42.5679 53.7757 43.9685 52.646 45.747C51.5168 47.5247 50.9438 49.599 51.0004 51.7043C51.0617 54.4557 52.1978 57.0738 54.1654 58.9981C56.133 60.9223 58.7757 61.9997 61.5277 61.9997Z"
          fill="var(--icon-element-fill, var(--brand-base)"
          stroke="var(--icon-circle-stroke, var(--white))"
          strokeWidth="var(--icon-stroke-width, 2)"
        />
        <path
          className="element element--plus"
          d="M62.7299 50.0697V46.0697C62.73 46.0448 62.7209 46.0208 62.7042 46.0023C62.6876 45.9838 62.6646 45.9722 62.6399 45.9697H60.3699C60.3434 45.9697 60.3179 45.9803 60.2992 45.999C60.2804 46.0178 60.2699 46.0432 60.2699 46.0697V50.0697C60.27 50.0824 60.2673 50.095 60.2622 50.1066C60.2571 50.1183 60.2495 50.1287 60.2401 50.1372C60.2306 50.1457 60.2195 50.1521 60.2074 50.156C60.1953 50.1599 60.1825 50.1611 60.1699 50.1597H56.2299C56.2034 50.1597 56.1779 50.1703 56.1592 50.189C56.1404 50.2078 56.1299 50.2332 56.1299 50.2597V52.5297C56.1299 52.5562 56.1404 52.5817 56.1592 52.6004C56.1779 52.6192 56.2034 52.6297 56.2299 52.6297H60.1699C60.1825 52.6283 60.1953 52.6296 60.2074 52.6335C60.2195 52.6374 60.2306 52.6438 60.2401 52.6523C60.2495 52.6608 60.2571 52.6712 60.2622 52.6828C60.2673 52.6944 60.27 52.707 60.2699 52.7197V56.7197C60.2683 56.7332 60.2698 56.747 60.2742 56.7598C60.2787 56.7727 60.286 56.7844 60.2956 56.794C60.3052 56.8036 60.3169 56.8109 60.3298 56.8154C60.3427 56.8199 60.3564 56.8213 60.3699 56.8197H62.6399C62.6526 56.8198 62.6652 56.8172 62.6768 56.8121C62.6884 56.8069 62.6988 56.7994 62.7073 56.7899C62.7158 56.7805 62.7222 56.7693 62.7261 56.7572C62.73 56.7451 62.7313 56.7324 62.7299 56.7197V52.7197C62.7324 52.695 62.744 52.672 62.7625 52.6554C62.781 52.6388 62.805 52.6296 62.8299 52.6297H66.8299C66.8556 52.6275 66.8798 52.6162 66.8981 52.5979C66.9164 52.5796 66.9276 52.5555 66.9299 52.5297V50.2597C66.9276 50.234 66.9164 50.2098 66.8981 50.1915C66.8798 50.1732 66.8556 50.162 66.8299 50.1597H62.8299C62.805 50.1598 62.781 50.1507 62.7625 50.1341C62.744 50.1174 62.7324 50.0945 62.7299 50.0697Z"
          fill="var(--icon-element-fill, var(--white))"
        />
      </symbol>
      <symbol id="svg-iron-compass" viewBox="0 0 46 46">
        <path
          className="element element--center-star"
          d="M28.1198 28.09L41.5298 22.8L28.1198 17.5L22.7998 4L17.4798 17.5L4.06982 22.8L17.4798 28.09L22.7998 41.59L28.1198 28.09ZM23.4498 36.39V23.42H36.4898L27.0998 27.13L23.4498 36.39ZM27.0998 18.46L36.4898 22.17H23.4498V9.2L27.0998 18.46ZM22.1398 9.2V22.17H9.09982L18.4898 18.46L22.1398 9.2ZM18.4998 27.13L9.09982 23.42H22.1398V36.39L18.4998 27.13Z"
          fill="var(--icon-element-fill, var(--gold-base))"
        />
        <path
          className="element element-circle"
          d="M44.7998 22.8C44.7998 10.67 34.9298 0.800049 22.7998 0.800049C10.6698 0.800049 0.799805 10.67 0.799805 22.8C0.799805 34.93 10.6698 44.8 22.7998 44.8C34.9298 44.8 44.7998 34.93 44.7998 22.8ZM2.0998 22.8C2.0998 11.36 11.3798 2.06005 22.7898 2.06005C34.1998 2.06005 43.4898 11.37 43.4898 22.8C43.4898 34.23 34.2098 43.5401 22.7898 43.5401C11.3898 43.5401 2.0998 34.23 2.0998 22.8Z"
          fill="var(--icon-element-fill, var(--gold-base))"
        />
      </symbol>
      <symbol id="svg-dumbbell" viewBox="0 0 100 100">
        <g transform="translate(0.000000, 15.000000)">
          <path
            className="element"
            d="M97.302874,32.6381724 C96.5556374,32.6204864 95.806927,32.6352248 94.9403095,32.6352248 L94.9403095,31.4170965 C94.9403095,27.0994842 94.9491525,22.7818718 94.9314665,18.4635225 C94.9285188,17.7111275 94.8879882,16.944731 94.7406043,16.2092852 C94.008843,12.5467944 91.1591746,10.1798084 87.3684598,10.1436993 C84.9071481,10.1201179 82.4450995,10.2196021 79.9012528,10.2652911 C79.9012528,9.3596168 79.9439941,8.32056006 79.8938836,7.28592483 C79.7184967,3.68386146 77.2173913,0.743551953 73.6403832,0.305084746 C71.3743552,0.027266028 69.0501105,0.118644068 66.7575534,0.197494473 C65.3271923,0.246868091 63.9882093,0.816507001 62.8658806,1.73618276 C60.8798821,3.36403832 60.0361091,5.52763449 60.0361091,8.05674282 C60.0361091,15.8415623 60.0361091,23.6256448 60.0361091,31.4104643 C60.0361091,31.7789241 60.0361091,32.1473839 60.0361091,32.5644805 L60.0361091,32.5644805 L60.0361091,37.6116433 L60.0361091,37.6116433 C60.0361091,40.3411938 60.0361091,42.9852616 60.0361091,45.6300663 C60.0361091,51.1768607 60.0140015,56.7236551 60.0442152,62.2704495 C60.0670597,66.4200442 62.577745,69.3478261 66.6963891,69.8754606 C68.559322,70.1142225 70.4708917,70.1046426 72.3529845,70.0316875 C76.4605748,69.8710391 79.3316139,67.2719234 79.8393515,63.2792926 C79.9882093,62.1068534 79.862196,60.9005158 79.862196,59.6499632 C80.2358143,59.6794399 80.4524687,59.6757553 80.66028,59.715549 C83.4443626,60.2505527 86.2593957,60.2424466 89.0228445,59.7826087 C92.6477524,59.1798084 94.9123066,56.0744289 94.9329403,51.9896831 C94.9557848,47.5460575 94.9373618,43.1024318 94.9410464,38.6580693 C94.9410464,38.2969786 94.9749447,37.935888 94.9963154,37.5261606 C95.8319823,37.5261606 96.5549005,37.5372144 97.2770818,37.5239499 C98.9417833,37.4922623 99.935888,36.5821665 99.9418091,35.0994842 C99.9476787,33.624171 98.9410464,32.6779661 97.3021371,32.6389094 L97.302874,32.6381724 Z M75.0103169,34.9926308 C75.0103169,43.9734709 75.0110538,52.954311 75.0103169,61.9351511 C75.0103169,64.1989683 74.1311717,65.1156964 71.889462,65.1171702 C70.2851879,65.1179071 68.6713338,65.1459101 67.0803242,64.9852616 C65.6742815,64.8430361 65.0147384,63.9594694 64.9528371,62.5453206 C64.9417833,62.294031 64.9491525,62.0412675 64.9491525,61.789241 C64.9491525,43.9535741 64.9491525,26.1179071 64.9491525,8.28297716 C64.9491525,5.84156227 65.7818718,5.01842299 68.2535004,5.01547531 C69.5460575,5.01400147 70.8386146,4.99852616 72.1311717,5.01989683 C74.0928519,5.0523213 75.008843,5.98747237 75.00958,7.95652174 C75.0140015,16.9690494 75.0110538,25.9808401 75.0110538,34.9933677 L75.0103169,34.9926308 Z M89.9948416,51.9182019 C89.9933677,54.3006632 89.2011791,55.092115 86.826087,55.1002211 C85.4708917,55.1046426 84.1156964,55.1223287 82.761238,55.0943257 C80.8430361,55.0545321 79.927045,54.1422255 79.9255711,52.2151805 C79.9145173,40.7811349 79.9152542,29.3478261 79.9255711,17.9137804 C79.927045,16.0338983 80.899042,15.0766396 82.7951363,15.0434783 C84.2439204,15.018423 85.6941783,15.0191599 87.1436993,15.0434783 C89.0663228,15.0744289 89.9859985,15.9830508 89.991157,17.9005158 C90.0058954,23.633014 89.9963154,29.3655122 89.9963154,35.0980103 C89.9970523,40.7044952 90,46.3109801 89.9963154,51.9182019 L89.9948416,51.9182019 Z"
            id="Shape"
          />
          <path
            className="element"
            d="M39.9587325,40.0685335 C39.9587325,39.6064849 39.9587325,39.1451732 39.9587325,38.6831245 L39.9587325,37.6116433 L39.9587325,37.6116433 L39.9587325,32.5644805 L39.9587325,32.5644805 L39.9587325,31.3743552 C39.9587325,30.9926308 39.9587325,30.6109064 39.9587325,30.229182 L39.9587325,29.0331614 L39.9587325,29.0331614 C39.9602063,22.0604274 39.9683125,15.0876934 39.9528371,8.11495947 C39.9432572,3.78703021 37.3655122,0.684598379 33.1591746,0.201915991 C31.0751658,-0.0368459838 28.9233604,-0.0235814296 26.83493,0.191599116 C22.963154,0.59100958 20.5836404,3.07442889 20.1164333,6.93441415 C19.9749447,8.10464259 19.9749447,9.29182019 19.9093589,10.4775239 C19.2011791,10.3588799 18.5571113,10.1724392 17.9093589,10.156227 C16.0817981,10.1112749 14.2512896,10.0987472 12.4244657,10.1436993 C10.7472366,10.1849668 9.19159912,10.7074429 7.86366986,11.7354458 C5.84819455,13.2962417 5.04421518,15.4797347 5.01915991,17.9432572 C4.97347089,22.449521 5.00589536,26.9565217 5.00589536,31.4635225 L5.00589536,32.6359617 C4.08032424,32.6359617 3.29624171,32.6109064 2.51436993,32.6411201 C1.06190125,32.6978629 0.197494473,33.4635225 0.0670596905,34.7641857 C-0.0950626382,36.385409 0.793662491,37.4369934 2.43699337,37.513633 C3.27192336,37.5526898 4.11053795,37.5202653 5.00589536,37.5202653 L5.00589536,38.6949153 C5.00589536,43.1702284 4.9476787,47.6470155 5.04347826,52.1201179 C5.07074429,53.3743552 5.34782609,54.7133382 5.86809138,55.8496684 C7.20338983,58.7678703 9.75755343,59.9454679 12.8297716,60.0081061 C15.1775976,60.0560059 17.5283714,59.9100958 19.9837878,59.8474576 C19.9837878,60.4988946 19.9705232,61.3109801 19.9859985,62.1230656 C20.064112,66.1164333 22.348563,69.1820192 26.0677966,69.7811349 C28.2520265,70.1326455 30.5298452,70.0928519 32.7516581,69.9719971 C35.0221076,69.8489315 37.0022108,68.9049374 38.4104643,66.9977892 C39.5445836,65.4620486 39.9454679,63.7022845 39.9491525,61.8459838 C39.9653648,54.641857 39.9579956,47.4369934 39.9572587,40.2328666 L39.9572587,40.2328666 L39.9572587,40.0677966 L39.9587325,40.0685335 Z M19.9830508,51.9557848 C19.981577,54.2815033 19.1798084,55.0891673 16.8725129,55.0994842 C15.486367,55.1061164 14.0987472,55.1260133 12.7133382,55.0928519 C10.8400884,55.0478998 9.92114959,54.1363301 9.91967576,52.2711864 C9.90788504,40.8054532 9.90862196,29.3404569 9.91820192,17.8747237 C9.91967576,16.0795873 10.8518791,15.1061164 12.6344878,15.0501105 C14.1768607,15.0014738 15.7229182,15.0058954 17.266028,15.0478998 C19.0803242,15.0980103 19.9742078,16.0243183 19.9786293,17.8422992 C19.991157,23.5747973 19.9830508,29.3080324 19.9830508,35.0405306 C19.9837878,40.678703 19.9859985,46.3168755 19.9830508,51.9550479 L19.9830508,51.9557848 Z M35.0007369,62.596168 C34.8150332,64.173913 34.2159175,64.9285188 32.633014,65.0346352 C30.7877671,65.1584377 28.9189388,65.1274871 27.0744289,64.981577 C25.7162859,64.8739867 25.173913,64.1753869 24.991157,62.8231393 C24.9322034,62.3883567 24.9211496,61.944731 24.9211496,61.50479 C24.917465,52.6823876 24.9182019,43.8599853 24.9182019,35.0375829 C24.9182019,26.2151805 24.9182019,17.4554164 24.9182019,8.66470155 C24.9182019,8.3183493 24.9056743,7.96978629 24.9329403,7.6256448 C25.064112,5.96315402 25.9130435,5.09137804 27.5858511,5.03021371 C29.1599116,4.97273397 30.7383935,4.97936625 32.3131909,5.02726603 C33.9896831,5.07811349 34.8496684,5.92778187 35.0110538,7.5843773 C35.0383198,7.86588062 35.0294768,8.15106853 35.0294768,8.43404569 C35.0302137,26.1731761 35.0302137,43.9130435 35.0294768,61.6521739 C35.0294768,61.9668386 35.036846,62.284451 35,62.5954311 L35.0007369,62.596168 Z"
            id="Shape"
          />
          <path
            className="element"
            d="M39.9718865,40.2343405 L39.9718865,40.2343405 C39.9718865,40.1790715 39.9718865,40.1238025 39.9718865,40.0692704 L39.9718865,40.2343405 Z"
            id="Path"
          />
          <path
            className="element"
            d="M39.9718865,30.229182 C39.9718865,29.8305085 39.9718865,29.4318349 39.9718865,29.0331614 L39.9718865,29.0331614 L39.9718865,30.229182 Z"
            id="Path"
          />
          <polygon
            className="element"
            id="Path"
            points="39.9587325 39.1908622 39.9587325 44.2380251 60.036846 44.2380251 60.036846 39.1908622"
          />
          <polygon
            className="element"
            id="Path"
            points="39.9587325 26.5099484 39.9587325 31.5571113 60.036846 31.5571113 60.036846 26.5099484"
          />
        </g>
      </symbol>
      <symbol id="svg-iron-compass-gradient" viewBox="0 0 46 46">
        <defs>
          <linearGradient
            id="compass_gradient_1"
            x1="24.8134"
            y1="25.57"
            x2="40.1694"
            y2="46.6845"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0.005618" stopColor="#EBC17D" />
            <stop offset="0.1854" stopColor="#DFB77C" />
            <stop offset="0.5955" stopColor="#C08329" />
            <stop offset="0.7976" stopColor="#DCAC68" />
            <stop offset="0.8708" stopColor="#EABE81" />
            <stop offset="1" stopColor="#EEDCC3" />
          </linearGradient>
          <linearGradient
            id="compass_gradient_2"
            x1="24.8135"
            y1="25.5699"
            x2="40.1695"
            y2="46.6844"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0.005618" stopColor="#EBC17D" />
            <stop offset="0.1854" stopColor="#DFB77C" />
            <stop offset="0.5955" stopColor="#C08329" />
            <stop offset="0.7976" stopColor="#DCAC68" />
            <stop offset="0.8708" stopColor="#EABE81" />
            <stop offset="1" stopColor="#EEDCC3" />
          </linearGradient>
        </defs>
        <path
          className="element element--center-circle"
          d="M44.7998 22.8C44.7998 10.67 34.9298 0.800049 22.7998 0.800049C10.6698 0.800049 0.799805 10.67 0.799805 22.8C0.799805 34.93 10.6698 44.8 22.7998 44.8C34.9298 44.8 44.7998 34.93 44.7998 22.8ZM2.0998 22.8C2.0998 11.36 11.3798 2.06005 22.7898 2.06005C34.1998 2.06005 43.4898 11.37 43.4898 22.8C43.4898 34.23 34.2098 43.5401 22.7898 43.5401C11.3898 43.5401 2.0998 34.23 2.0998 22.8Z"
          fill="url(#compass_gradient_1)"
        />
        <path
          className="element element--star"
          d="M28.1198 28.09L41.5298 22.8L28.1198 17.5L22.7998 4L17.4798 17.5L4.06982 22.8L17.4798 28.09L22.7998 41.59L28.1198 28.09ZM23.4498 36.39V23.42H36.4898L27.0998 27.13L23.4498 36.39ZM27.0998 18.46L36.4898 22.17H23.4498V9.2L27.0998 18.46ZM22.1398 9.2V22.17H9.09982L18.4898 18.46L22.1398 9.2ZM18.4998 27.13L9.09982 23.42H22.1398V36.39L18.4998 27.13Z"
          fill="url(#compass_gradient_2)"
        />
      </symbol>
    </svg>
  )
  /* eslint-enable react/no-unknown-property */
}

export default IconData
